import type { DataSource } from "../../types.ts";
import type { UserLibraryItem } from "../../services/ApiService.ts";

const MODULE_PREFIXES = ["bi_", "fw_", "ef_", "p4f_", "rf_", "wm_", "rl_"];

// Above WARN_ROWS the fetch is slow but works; above MAX_ROWS or MAX_CELLS the backend
// refuses it (see check_selection_size in data_studio_shared.py — keep the numbers in step).
export const WARN_ROWS = 10_000;
export const MAX_ROWS = 50_000;
export const MAX_CELLS = 2_000_000;

/** Message explaining why a stored library item is too large to open, or null when it fits. */
export const libraryItemSizeError = (item: UserLibraryItem): string | null => {
  if (item.row_count == null) return null;
  if (item.row_count > MAX_ROWS) {
    return (
      `Deze bron bevat ${item.row_count.toLocaleString("nl-NL")} rijen. Dat is te veel om te openen — ` +
      `het maximum is ${MAX_ROWS.toLocaleString("nl-NL")} rijen. Maak een nieuwe, kleinere selectie ` +
      `met een kortere periode of een extra filter.`
    );
  }
  if (item.row_count * item.fields.length > MAX_CELLS) {
    return (
      `Deze bron bevat ${item.row_count.toLocaleString("nl-NL")} rijen met ${item.fields.length} kolommen. ` +
      `Dat is te veel om te openen — het maximum is ${MAX_CELLS.toLocaleString("nl-NL")} velden in totaal. ` +
      `Maak een nieuwe selectie met minder kolommen of een kortere periode.`
    );
  }
  return null;
};

/** Strips the "ds_" prefix and the source-system prefix from a module id. */
export const cleanModuleName = (module: string): string => {
  if (!module.startsWith("ds_")) return module;
  const withoutDs = module.substring(3);
  const prefix = MODULE_PREFIXES.find((p) => withoutDs.startsWith(p));
  return prefix ? withoutDs.substring(prefix.length) : withoutDs;
};

/** "ds_bi_gewerkte_weken" -> "Gewerkte weken". */
export const formatModuleLabel = (module: string): string =>
  cleanModuleName(module).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Turns a stored library item into a data source the chat can use. */
export const libraryItemToDataSource = (item: UserLibraryItem): DataSource => {
  const baseName = cleanModuleName(item.module);
  const paramParts = Object.entries(item.parameters)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}_${value}`)
    .join("_");
  return {
    endpoint_name: item.module,
    source_name: paramParts ? `${baseName}_${paramParts}` : baseName,
    selected_fields: item.fields,
    parameters: item.parameters,
    library_item_id: item.id,
    row_count: item.row_count,
  };
};

/** Short parameter summary for a chip, e.g. "jaar: 2025 +1". */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
export const summarizeParameters = (parameters: Record<string, any>): string => {
  const entries = Object.entries(parameters);
  if (entries.length === 0) return "";
  const shown = entries
    .slice(0, 2)
    .map(([key, value]) => `${key.split("_").pop()}: ${value}`)
    .join(" · ");
  return entries.length > 2 ? `${shown} +${entries.length - 2}` : shown;
};
